import { useEffect, useState } from 'react';
import { Pagination } from '../../../components/ui/Pagination';
import { adminApi, type AnnouncementAudience } from '../../../services/admin';
import adminStyles from '../Admin.module.css';
import styles from './AnnouncementsTab.module.css';

interface AudiencePreviewProps {
    planIds: string[];
    signupFrom: string;
    signupTo: string;
}

const formatSignup = (value: string) => new Date(value).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });

/** Mostra quem receberia se o comunicado saisse agora. */
export function AudiencePreview({ planIds, signupFrom, signupTo }: AudiencePreviewProps) {
    const planIdsKey = planIds.join(',');
    const filterKey = `${planIdsKey}|${signupFrom}|${signupTo}`;
    const invalidRange = !!signupFrom && !!signupTo && signupFrom > signupTo;

    const [paging, setPaging] = useState({ filterKey, page: 1 });
    const [result, setResult] = useState<AnnouncementAudience | null>(null);
    const [failed, setFailed] = useState(false);
    const [loading, setLoading] = useState(true);
    // Filtro novo sempre volta para a primeira pagina.
    const page = paging.filterKey === filterKey ? paging.page : 1;

    useEffect(() => {
        if (invalidRange) return;
        let cancelled = false;
        setLoading(true);
        adminApi
            .getAnnouncementAudience(
                { planIds: planIdsKey ? planIdsKey.split(',') : [], signupFrom, signupTo },
                page,
            )
            .then((response) => {
                if (cancelled) return;
                setResult(response);
                setFailed(false);
            })
            .catch(() => {
                if (!cancelled) setFailed(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [invalidRange, planIdsKey, signupFrom, signupTo, page]);

    if (invalidRange) {
        return <span className={styles.errorHint}>A data inicial do cadastro não pode ser depois da final.</span>;
    }
    if (failed) {
        return <span className={styles.errorHint}>Não foi possível carregar os usuários deste público.</span>;
    }
    if (!result) {
        return <span className={styles.hint}>Carregando usuários…</span>;
    }

    const { total, limit, totalPages } = result.pagination;
    return (
        <div className={`${styles.audience} ${loading ? styles.audienceLoading : ''}`} aria-busy={loading}>
            <span className={styles.hint}>
                {total} {total === 1 ? 'usuário recebe' : 'usuários recebem'} hoje.
            </span>
            <div className={styles.tableWrap}>
                <table className={adminStyles.usersTable}>
                    <thead>
                        <tr>
                            <th>Nome</th>
                            <th>E-mail</th>
                            <th>Plano</th>
                            <th>Cadastro</th>
                        </tr>
                    </thead>
                    <tbody>
                        {result.data.length === 0 ? (
                            <tr>
                                <td colSpan={4} className={adminStyles.emptyRow}>Nenhum usuário ativo neste público.</td>
                            </tr>
                        ) : (
                            result.data.map((user) => (
                                <tr key={user.id}>
                                    <td>{user.name}</td>
                                    <td>{user.email}</td>
                                    <td>{user.planName ?? '-'}</td>
                                    <td>{formatSignup(user.createdAt)}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={total}
                itemsPerPage={limit}
                onPageChange={(next) => setPaging({ filterKey, page: next })}
            />
        </div>
    );
}
